import { type ApiError as ApiErrorEnvelope, type AuthTokens, ErrorCode } from '@apm/contracts';
import { orgRestructuringStore } from './org-restructuring';
import { tokenStore } from './token-store';

const API_PREFIX = '/api/v1';

/**
 * Lỗi API đã bóc vỏ. Component `switch` trên `code`, **không bao giờ** đọc
 * `message` để quyết định — docs/api-conventions.md §3.
 */
export class ApiRequestError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /**
   * Body dạng **object**, chưa serialize — `apiFetch` tự `JSON.stringify`.
   *
   * ⚠️ Kiểu hẹp lại từ `unknown` có chủ đích: với `unknown` thì
   * `body: JSON.stringify(input)` biên dịch được, và nó gửi lên một **chuỗi
   * JSON** thay vì object. Server nhận chuỗi ⇒ Zod trả 400 cho mọi trường, và
   * người dùng thấy "Dữ liệu chưa hợp lệ" mà không hiểu vì sao. Đã cắn ở
   * APM-805 với ba endpoint mới (key AI, export, link chia sẻ).
   */
  body?: Record<string, unknown> | readonly unknown[] | undefined;
  /**
   * Tắt cơ chế tự refresh. Lời gọi `/auth/refresh` bắt buộc đặt cờ này — thiếu nó
   * thì refresh hỏng sẽ tự gọi lại refresh, vô hạn. Ngoài ra chỉ đặt cho endpoint mà
   * 401 là câu trả lời nghiệp vụ và gửi lại có hậu quả (`/auth/login`: sai mật khẩu).
   */
  skipAuthRefresh?: boolean;
}

/**
 * Một lời gọi refresh duy nhất tại một thời điểm.
 *
 * Dashboard mở ra bắn 6 query song song; access token hết hạn thì cả 6 cùng
 * nhận 401. Không gom lại thì có 6 lời gọi `/auth/refresh` — mà refresh token
 * **xoay mỗi lần dùng**, nên lời gọi thứ hai trình ra token đã tiêu và server
 * hiểu đúng đó là dấu hiệu bị đánh cắp: thu hồi sạch phiên và đá người dùng ra.
 *
 * Nói cách khác: thiếu single-flight ở đây thì tính năng chống trộm token của
 * APM-102 tự bắn vào chân mình.
 */
let inFlightRefresh: Promise<string | null> | null = null;

export function refreshSession(): Promise<string | null> {
  inFlightRefresh ??= (async () => {
    try {
      const tokens = await apiFetch<AuthTokens>('/auth/refresh', {
        method: 'POST',
        skipAuthRefresh: true,
      });
      tokenStore.set(tokens.accessToken);
      return tokens.accessToken;
    } catch {
      // Refresh hỏng = hết phiên thật. Xoá token để `AuthProvider` (đang lắng
      // nghe tokenStore) chuyển sang trạng thái chưa đăng nhập.
      tokenStore.set(null);
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

/**
 * Token mà một lượt làm mới **vì `UNAUTHENTICATED`** vừa cấp. Chính token ấy lại nhận
 * `UNAUTHENTICATED` thì làm mới lần nữa cũng vô ích — server vừa cấp nó và vẫn từ chối
 * (thường là handler tự ném 401 vì lý do khác phiên). Không có mốc này thì mỗi query mới
 * lại làm mới một lần: không vô hạn trong một lời gọi, nhưng thành một cơn bão refresh.
 */
let tokenFromUnauthRefresh: string | null = null;

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  // `send` đọc token đồng bộ ngay lúc gọi, nên đây đúng là token đi kèm request.
  const sentToken = tokenStore.get();
  const response = await send(path, options);

  /**
   * Đây là toàn bộ nội dung của "phiên hỏng thì tự làm mới, không đá user ra oan".
   *
   * - `TOKEN_EXPIRED`: access token hết hạn — làm mới rồi chạy lại.
   * - `UNAUTHENTICATED` **khi request có mang token**: token còn hạn nhưng server không
   *   nhận nữa — tư cách thành viên vừa bị khoá (APM-MSUS-b, `MembershipGate`), tổ chức
   *   vừa bị gộp và xoá mềm (APM-ORGMS-e), user bị vô hiệu hoá. Làm mới đúng một lần:
   *   được thì chạy lại với token mới; hỏng thì `refreshSession()` xoá token và
   *   `AuthProvider` đưa về trang đăng nhập. Không làm mới thì người dùng đứng ở trạng
   *   thái lỗi tới khi access token tự hết hạn (15 phút).
   * - `UNAUTHENTICATED` khi **không** mang token (chưa đăng nhập, link chia sẻ, sai mật
   *   khẩu ở trang login): không có phiên nào để làm mới — ném ngay.
   */
  if (response.status === 401 && !options.skipAuthRefresh) {
    const error = await readError(response);
    const refreshable =
      error.code === ErrorCode.TOKEN_EXPIRED ||
      (error.code === ErrorCode.UNAUTHENTICATED &&
        sentToken !== null &&
        sentToken !== tokenFromUnauthRefresh);

    if (refreshable) {
      // Request khác đã làm mới xong trong lúc request này bay ⇒ dùng luôn token ấy,
      // không tiêu thêm một refresh token.
      const current = tokenStore.get();
      const token = current !== null && current !== sentToken ? current : await refreshSession();
      if (token) {
        if (error.code === ErrorCode.UNAUTHENTICATED) tokenFromUnauthRefresh = token;
        // Retry đúng **một** lần: nếu vẫn 401 sau khi vừa có token mới thì
        // vấn đề không phải phiên, và vòng lặp retry sẽ che mất lỗi thật.
        return parse<T>(await send(path, { ...options, skipAuthRefresh: true }));
      }
    }

    throw error;
  }

  return parse<T>(response);
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { body, skipAuthRefresh: _skip, headers, ...rest } = options;
  const token = tokenStore.get();

  return fetch(`${API_PREFIX}${path}`, {
    ...rest,
    // Refresh token nằm trong cookie httpOnly; không gửi cookie thì
    // `/auth/refresh` không bao giờ thành công.
    credentials: 'include',
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

async function parse<T>(response: Response): Promise<T> {
  if (!response.ok) throw await readError(response);

  // 204 và body rỗng: `response.json()` sẽ ném lỗi cú pháp trên chuỗi rỗng.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function readError(response: Response): Promise<ApiRequestError> {
  let envelope: ApiErrorEnvelope | undefined;
  try {
    envelope = (await response.json()) as ApiErrorEnvelope;
  } catch {
    // Proxy chết, 502 từ nginx, mất mạng giữa chừng — body không phải JSON.
    envelope = undefined;
  }

  const error = envelope?.error;
  const code = error?.code ?? ErrorCode.INTERNAL;
  // Tổ chức đang gộp / tách — APM-ORGMS-f. Vỏ `_app` thay trang bằng màn hình chờ
  // (`orgRestructuringStore`), nên không màn hình nào phải tự bắt mã này.
  if (response.status === 423 && code === ErrorCode.ORG_RESTRUCTURING) orgRestructuringStore.mark();
  return new ApiRequestError(
    code,
    error?.message ?? 'Không kết nối được máy chủ. Vui lòng thử lại.',
    response.status,
    error?.details,
  );
}

/** Chỉ dùng trong test — xoá lời gọi refresh đang treo giữa hai test case. */
export function resetHttpState(): void {
  inFlightRefresh = null;
  tokenFromUnauthRefresh = null;
}
